/* ==========================================================================
   LUX — Wishlist (core)

   Stored in localStorage. No app, no account required, nothing to break at
   checkout. Products are keyed by handle so the list survives re-imports.

   This file owns the STATE and the heart buttons on product cards.
   The wishlist PAGE (/pages/wishlist) is rendered by
   assets/lux-wishlist-page.js using sections/lux-wishlist-item.liquid.
   ========================================================================== */

(function () {
  'use strict';

  var KEY = 'lux:wishlist';

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      // Defensive: only keep non-empty strings. A corrupted entry would
      // otherwise render an empty card forever with no way to remove it.
      return parsed.filter(function (h) {
        return typeof h === 'string' && h.length > 0;
      });
    } catch (e) {
      // Private mode / storage disabled — degrade to an in-memory list.
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) { /* nothing we can do; the UI still updates for this page */ }
  }

  function has(handle) {
    return read().indexOf(handle) !== -1;
  }

  function emit(handle, active) {
    document.dispatchEvent(new CustomEvent('lux:wishlist:change', {
      detail: { handle: handle, active: active, items: read() }
    }));
  }

  function toggle(handle) {
    var list = read();
    var i = list.indexOf(handle);

    if (i === -1) {
      list.push(handle);
    } else {
      list.splice(i, 1);
    }

    write(list);
    return i === -1;
  }

  /* Remove one handle without toggling it back on. Used by the wishlist
     page's Remove button and to prune products that no longer resolve. */
  function remove(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i === -1) return false;

    list.splice(i, 1);
    write(list);
    paintAll(document);
    emit(handle, false);
    return true;
  }

  function paintButton(btn, active) {
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('lux-card__action--active', active);

    var off = btn.querySelector('[data-wishlist-off]');
    var on = btn.querySelector('[data-wishlist-on]');
    if (off) off.hidden = active;
    if (on) on.hidden = !active;

    var title = btn.getAttribute('aria-label') || '';
    if (active && title.indexOf('Remove') !== 0) {
      btn.setAttribute('aria-label', title.replace(/^Add /, 'Remove ').replace(' to wishlist', ' from wishlist'));
    } else if (!active && title.indexOf('Remove') === 0) {
      btn.setAttribute('aria-label', title.replace(/^Remove /, 'Add ').replace(' from wishlist', ' to wishlist'));
    }
  }

  function paintCount() {
    var count = read().length;

    document.querySelectorAll('[data-lux-wishlist-count]').forEach(function (el) {
      var span = el.querySelector('span');
      if (span) span.textContent = count;
      el.hidden = count === 0;
    });
  }

  function paintAll(scope) {
    var list = read();

    (scope || document).querySelectorAll('[data-lux-wishlist-toggle]').forEach(function (btn) {
      paintButton(btn, list.indexOf(btn.getAttribute('data-product-handle')) !== -1);
    });

    paintCount();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-lux-wishlist-toggle]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    var nowActive = toggle(handle);
    paintButton(btn, nowActive);
    paintCount();

    // Keep duplicate cards for the same product in sync.
    document.querySelectorAll('[data-lux-wishlist-toggle][data-product-handle="' + handle + '"]').forEach(function (other) {
      if (other !== btn) paintButton(other, nowActive);
    });

    if (window.LuxCart && window.LuxCart.toast) {
      window.LuxCart.toast(nowActive ? 'Saved to your wishlist' : 'Removed from your wishlist');
    }

    emit(handle, nowActive);
  });

  // Another tab changed the list.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) paintAll(document);
  });

  function init(scope) { paintAll(scope || document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.LuxWishlist = {
    key: KEY,
    items: read,
    has: has,
    toggle: toggle,
    remove: remove,
    refresh: function () { paintAll(document); }
  };
})();
